/**
 * 客服舱坐席侧「实时」规则(1010 一期补完 B2–B4)—— 纯函数,单测直接测。
 *
 * ★为什么要轮询:访客在网页 / H5 上发消息、AI 转人工都发生在服务端,控制台没有推送通道;
 *   坐席不刷新就看不见 → 访客干等。页面隐藏时 react-query 自动暂停 refetchInterval(默认不在后台轮询)。
 */

/** 企微客服渠道:后端落库 `wecom_kf`,部分投影是 `wecom` —— 判宽不判窄(见 SessionDetailCard 2026-08-07 注释)。 */
export const isWecomChannel = (channel?: string | null): boolean =>
  (channel ?? "").startsWith("wecom");

/** 会话列表:页面可见时每 15s 刷新。 */
export const SESSIONS_POLL_MS = 15_000;

/**
 * 打开中的会话消息刷新间隔:非企微(网页 / H5)且人工处理中 4s(访客在等人)、AI 接待中 10s(随时可能转人工);
 * 企微 / 已结束 / 未知 → 不轮询(企微转人工后在企微工作台回,本平台只读)。
 */
export function messagesPollMs(
  detail: { channel?: string | null; status?: string | null } | undefined
): number | false {
  if (!detail || isWecomChannel(detail.channel)) return false;
  if (detail.status === "human_handling") return 4_000;
  if (detail.status === "active") return 10_000;
  return false;
}

/** 接管 / 接手按钮怎么走:企微 = 弹「选择接待人员」;其它渠道 = 直接调接口,接管人 = 当前登录人。 */
export type TakeoverMode = "wecom_dialog" | "direct";
export function takeoverModeOf(channel?: string | null): TakeoverMode {
  return isWecomChannel(channel) ? "wecom_dialog" : "direct";
}

/** 列表行「接手」:人工处理中、还没人接、且不是企微(企微转人工后由企微接待人员处理)。 */
export function canPickup(s: {
  status: string;
  channel?: string | null;
  seatUserId?: number | null;
}): boolean {
  return s.status === "human_handling" && !isWecomChannel(s.channel) && s.seatUserId == null;
}

/** 标题前缀「(N) 」:N>0 时加;先剥掉旧前缀,反复调用不叠加。 */
const PENDING_PREFIX_RE = /^\(\d+\)\s/;
export function titleWithPending(title: string, n: number): string {
  const base = title.replace(PENDING_PREFIX_RE, "");
  return n > 0 ? `(${n}) ${base}` : base;
}

/** 提示音开关(默认关,只记在本浏览器;存储不可用时当关)。 */
export const PENDING_SOUND_STORAGE_KEY = "kefu:pending-sound:v1";
export function readPendingSoundPref(): boolean {
  try {
    return localStorage.getItem(PENDING_SOUND_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}
export function writePendingSoundPref(on: boolean): void {
  try {
    localStorage.setItem(PENDING_SOUND_STORAGE_KEY, on ? "1" : "0");
  } catch {
    /* 隐私模式 / 存储被禁:只在本次页面内生效 */
  }
}

/** 有新的等待会话才响:数量上升才算「新」(下降 = 被接走,不响)。 */
export function shouldChime(prev: number | undefined, next: number | undefined): boolean {
  return prev !== undefined && next !== undefined && next > prev;
}

/** 短促两声提示(WebAudio 合成,不引音频文件);浏览器不支持 / 未交互禁播时静默失败。 */
export function playPendingChime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.18].forEach((offset, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = i === 0 ? 880 : 1175;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.17);
    });
    window.setTimeout(() => void ctx.close().catch(() => undefined), 600);
  } catch {
    /* 静默 */
  }
}
